import { useActionCreators } from 'app/area/case-report/case-listing/hooks';
import { selectLayoutTypes } from 'app/area/case-report/case-listing/selectors';
import { selectLayoutTypeAttributes as selectCaseTableLayoutAttributes} from 'app/area/case-report/case-table-view/selectors';
import { selectLayoutTypeAttributes as selectCaseListingLayoutAttributes } from 'app/area/case-report/case-listing/selectors';
import { dashboardLayoutTypeContext } from 'app/area/dashboard/constants';
import {
  useAutoExecuteContext, useInstanceKey, useInstanceOptions
} from 'app/area/favourite/favourite-context';
import { selectLayoutTypeForSelectedReport, selectSelectedReport } from 'app/area/favourite/selectors';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

/** Returned while in print mode but the layout attributes have not loaded yet. */
export const PRINT_PENDING = Symbol('print-pending');

/**
 * Hook to get the maximum number of results for print mode from layout type attributes.
 * @param attributeName The name of the attribute in layoutTypeAttributes to read the print max value from
 * @returns undefined when not in print mode, PRINT_PENDING while attributes load, otherwise the max results
 */
export function usePrintMaxResults(
  attributeName: string
): number | undefined | typeof PRINT_PENDING {
  const instanceKey = useInstanceKey();
  const { layoutTypeAttributes: contextLayoutAttributes, layoutTypeContextId } = useInstanceOptions();

  // In non-dashboard context, get attributes from Redux state
  const caseTableLayoutAttributes = useSelector(selectCaseTableLayoutAttributes(instanceKey));
  const caseListingLayoutAttributes = useSelector(selectCaseListingLayoutAttributes(instanceKey));

  // Use context attributes for dashboard, Redux attributes for search/report context
  const layoutTypeAttributes = layoutTypeContextId === dashboardLayoutTypeContext
    ? contextLayoutAttributes
    : caseTableLayoutAttributes || caseListingLayoutAttributes;

  return useMemo(() => {
    const isPrintMode = new URLSearchParams(window.location.search).get('print') === 'true';

    if (!isPrintMode) {
      return undefined;
    }

    if (!layoutTypeAttributes) {
      return PRINT_PENDING;
    }

    const parsed = parseInt(layoutTypeAttributes[attributeName] as string, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [layoutTypeAttributes, attributeName]);
}

export function useAutoExecuteState() {
  const autoExecuteContext = useAutoExecuteContext();
  const [autoExecute, setAutoExecute] = useState(autoExecuteContext.current);

  const toggleAutoExecution = useCallback(() => {
    setAutoExecute(!autoExecute);
    autoExecuteContext.current = !autoExecute;
  }, [autoExecute]);

  return {
    autoExecute,
    toggleAutoExecution
  };
}

export function useLayoutTypesEffect() {
  const instanceKey = useInstanceKey();
  const { hideVisualizationOptions } = useInstanceOptions();
  const selectedReport = useSelector(selectSelectedReport(instanceKey));
  const layoutTypes = useSelector(selectLayoutTypes(instanceKey));
  const selectedLayoutType = useSelector(selectLayoutTypeForSelectedReport(instanceKey));
  const { layoutTypeContextId } = useInstanceOptions();
  const { loadLayoutTypes, setLayoutType } = useActionCreators();

  useEffect(() => {
    if (layoutTypeContextId !== dashboardLayoutTypeContext) {
      loadLayoutTypes(selectedReport?.v);
    }
  }, [loadLayoutTypes, selectedReport?.v]);

  return {
    layoutTypes,
    selectedLayoutType: selectedLayoutType ? codeItemToStoreValue(selectedLayoutType) : null,
    setLayoutType,
    hideVisualization: hideVisualizationOptions,
    layoutTypeContextId
  };
}
